import type { Drawing2D, Point2, Polyline2D } from './viewTypes'

// ---------------------------------------------------------------------------
// Object snap (osnap) sobre o desenho 2D.
//
// Medir "quase" um canto não serve para nada: a cota que sai é um número
// bonito e errado. O snap agarra o cursor à geometria do desenho, como
// em qualquer CAD, e o clique passa a usar o ponto EXACTO do desenho em
// vez do ponto do rato.
//
// Alvos, por ordem de prioridade quando empatam:
//   1. vértices/extremos das polilinhas
//   2. intersecções entre segmentos de polilinhas
//   3. pontos médios dos segmentos
//   4. centros de círculos e arcos (o IR reconhece-os desde a sexta leva)
//
// Porque é que a intersecção fica em segundo e não em primeiro: onde há
// vértice E intersecção (duas paredes que se encostam pelo canto, um T)
// o ponto é o mesmo, e o vértice é o alvo EXACTO do desenho, enquanto a
// intersecção é um número calculado. Ganhando o vértice, a cota sai do
// desenho e não da aritmética, e nunca se mostram duas marcas no mesmo
// sítio. Fica à frente do ponto médio porque quem aproxima o cursor de
// um cruzamento quer o cruzamento, não o meio de uma das linhas.
//
// Arcos e círculos NÃO entram nas intersecções desta leva: cruzar recta
// com arco (e arco com arco) é outra família de casos degenerados
// (tangências, dois pontos, troços fora do sector) e não vale a pena
// misturá-la com a parte recta, que é o que resolve as paredes.
//
// Desempenho: uma planta tem facilmente dezenas de milhares de vértices e
// o snap corre a cada movimento do rato. Um varrimento linear por
// movimento seria O(n) e sente-se. Daí a grelha uniforme: constrói-se uma
// vez por desenho e cada consulta olha só para as células que o disco de
// procura toca. As intersecções usam a MESMA grelha, com os segmentos
// registados em todas as células que atravessam; por consulta cruzam-se
// só os poucos segmentos que passam dentro do disco, e não todos os
// pares do desenho (que seria O(n²) e impensável).
//
// Tudo aqui é puro: números, sem DOM, sem React, sem three.
// ---------------------------------------------------------------------------

/** Tipo de ponto agarrado. A ordem da união é também a ordem de
 *  prioridade (ver `SNAP_RANK`). */
export type SnapKind = 'vertex' | 'intersection' | 'midpoint' | 'center'

export interface SnapPoint {
  kind: SnapKind
  p: Point2
}

/** Prioridade: menor ganha em caso de empate na distância. Um vértice é
 *  quase sempre o que o utilizador quer; o centro de um círculo é o que
 *  menos vezes quer. */
export const SNAP_RANK: Record<SnapKind, number> = {
  vertex: 0,
  intersection: 1,
  midpoint: 2,
  center: 3,
}

/** Tecto de pontos indexados. Um modelo patológico não pode encher a
 *  memória do browser por causa de uma ajuda ao desenho; passado o
 *  tecto, o índice fica com o que apanhou (`truncated`). */
export const SNAP_MAX_POINTS = 200_000

/** Tecto de segmentos indexados, pela mesma razão do tecto de pontos. */
export const SNAP_MAX_SEGMENTS = 120_000

/** Tecto de células que UM segmento pode ocupar. Uma diagonal que
 *  atravesse o desenho todo tocaria centenas de células; passado o
 *  tecto, o resto do segmento fica de fora do índice (perde-se alguma
 *  intersecção numa ponta, não se perde o interactivo). */
const MAX_CELLS_PER_SEGMENT = 256

/** Tecto de segmentos recolhidos por consulta antes de os cruzar. O
 *  custo dos pares é QUADRÁTICO: com 24 são 276 cruzamentos, com 48
 *  seriam 1128. Numa planta com a projecção tesselada, um disco de 10
 *  px chega a apanhar dezenas de segmentos minúsculos, e a diferença
 *  entre os dois tectos mede-se no orçamento do movimento do rato.
 *  Quando o tecto aperta ficam os segmentos MAIS PRÓXIMOS do cursor,
 *  que são os que podem dar a intersecção que interessa. */
const MAX_SEGS_PER_QUERY = 24

/** Quantização (metros) para juntar pontos coincidentes vindos de
 *  polilinhas diferentes. Um micrómetro: muito abaixo de qualquer
 *  tolerância de construção e muito acima do ruído da vírgula
 *  flutuante. */
const DEDUPE_Q = 1e-6

/** Duas distâncias dentro disto contam como empate, e aí decide a
 *  prioridade em vez do acaso da vírgula flutuante. */
const TIE_EPS = 1e-9

/** Alvo de ocupação média por célula. Baixo demais gasta memória em
 *  células vazias, alto demais volta a aproximar-se do varrimento
 *  linear. */
const TARGET_PER_CELL = 4

/** Tecto de células varridas por consulta. Só se atinge com o desenho
 *  todo enquadrado num selo, onde o snap não tem significado nenhum;
 *  vale mais devolver depressa do que varrer a grelha inteira. */
const MAX_CELLS_PER_QUERY = 4096

/** Segmentos rectos do desenho, na MESMA grelha dos pontos (mesma
 *  origem, mesmo lado de célula), para não haver duas geometrias a
 *  manter em sincronia. Um segmento aparece em todas as células que
 *  atravessa, e não só nas das pontas: senão, o cruzamento a meio de
 *  uma parede longa nunca seria encontrado. */
export interface SnapSegmentIndex {
  /** Extremos, 4 números por segmento: ax, ay, bx, by. */
  coords: Float64Array
  /** Chave = linha * cols + coluna → índices de segmento. */
  buckets: Map<number, number[]>
  count: number
  /** true = bateu no `SNAP_MAX_SEGMENTS` e ficou incompleto. */
  truncated: boolean
  /** Campo de TRABALHO da consulta: marca da última visita a cada
   *  segmento. Um segmento está em várias células, e sem marca era
   *  preciso procurá-lo na lista já recolhida (linear, e a consulta
   *  passava a quadrática no número de candidatos). */
  seen: Int32Array
  /** Número da consulta actual. Cresce a cada `resolveSnap`. */
  seenTick: number
}

export interface SnapIndex {
  /** Lado da célula, em unidades do desenho (metros). */
  cell: number
  minX: number
  minY: number
  cols: number
  rows: number
  /** Chave = linha * cols + coluna. Só as células com pontos existem. */
  buckets: Map<number, SnapPoint[]>
  /** Pontos indexados (depois de juntar coincidentes). */
  count: number
  /** true = bateu no `SNAP_MAX_POINTS` e ficou incompleto. */
  truncated: boolean
  /** Segmentos, para as intersecções. */
  segments: SnapSegmentIndex
}

function emptySegmentIndex(): SnapSegmentIndex {
  return {
    coords: new Float64Array(0),
    buckets: new Map(),
    count: 0,
    truncated: false,
    seen: new Int32Array(0),
    seenTick: 0,
  }
}

/** Índice vazio: consultar dá sempre null. Serve de valor neutro para
 *  quem ainda não tem desenho. */
export function emptySnapIndex(): SnapIndex {
  return {
    cell: 1,
    minX: 0,
    minY: 0,
    cols: 1,
    rows: 1,
    buckets: new Map(),
    count: 0,
    truncated: false,
    segments: emptySegmentIndex(),
  }
}

function isFinitePoint(p: Point2 | undefined): p is Point2 {
  return (
    Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1])
  )
}

/** Percorre os alvos de uma polilinha. Não devolve lista: quem chama
 *  acumula no dicionário e assim não se criam milhares de arrays
 *  intermédios. `emitSeg` recebe cada segmento recto (o mesmo troço que
 *  deu o ponto médio), para o índice das intersecções. */
function eachPolylineTarget(
  pl: Polyline2D,
  emit: (kind: SnapKind, x: number, y: number) => void,
  emitSeg: (ax: number, ay: number, bx: number, by: number) => void,
): void {
  const pts = pl?.points
  if (!Array.isArray(pts) || pts.length === 0) return
  const n = pts.length
  for (let i = 0; i < n; i++) {
    const a = pts[i]
    if (!isFinitePoint(a)) continue
    emit('vertex', a[0], a[1])
    // Segmento a->b: o de fecho só existe se a polilinha for fechada e
    // tiver mais de dois pontos (com dois, fechar era percorrer o mesmo
    // segmento ao contrário).
    const isLast = i === n - 1
    if (isLast && (!pl.closed || n <= 2)) continue
    const b = pts[isLast ? 0 : i + 1]
    if (!isFinitePoint(b)) continue
    emit('midpoint', (a[0] + b[0]) / 2, (a[1] + b[1]) / 2)
    // Segmentos degenerados (as duas pontas no mesmo sítio) não têm
    // direcção e nunca cruzam nada: ficam fora do índice.
    if (a[0] !== b[0] || a[1] !== b[1]) emitSeg(a[0], a[1], b[0], b[1])
  }
  if (pl.circle && Number.isFinite(pl.circle.cx) && Number.isFinite(pl.circle.cy)) {
    emit('center', pl.circle.cx, pl.circle.cy)
  }
  if (Array.isArray(pl.arcs)) {
    for (const arc of pl.arcs) {
      if (!arc || !Number.isFinite(arc.cx) || !Number.isFinite(arc.cy)) continue
      emit('center', arc.cx, arc.cy)
    }
  }
}

/** Constrói o índice a partir do desenho VISÍVEL (camadas escondidas não
 *  devem agarrar o cursor: se não se vê, não se agarra). */
export function buildSnapIndex(drawing: Drawing2D | null): SnapIndex {
  if (!drawing || !Array.isArray(drawing.layers)) return emptySnapIndex()

  // 1.ª passagem: recolher e juntar coincidentes. Onde um vértice e um
  // ponto médio caem no mesmo sítio fica o de maior prioridade, senão o
  // índice devolvia empates que já sabíamos resolver.
  const seen = new Map<string, SnapPoint>()
  let truncated = false
  const emit = (kind: SnapKind, x: number, y: number) => {
    if (truncated) return
    if (!Number.isFinite(x) || !Number.isFinite(y)) return
    const key = `${Math.round(x / DEDUPE_Q)}|${Math.round(y / DEDUPE_Q)}`
    const prev = seen.get(key)
    if (prev) {
      if (SNAP_RANK[kind] < SNAP_RANK[prev.kind]) prev.kind = kind
      return
    }
    if (seen.size >= SNAP_MAX_POINTS) {
      truncated = true
      return
    }
    seen.set(key, { kind, p: [x, y] })
  }
  // Os segmentos vão para um array cru nesta passagem: a grelha onde
  // eles entram só é conhecida depois de se saber a caixa dos pontos.
  const rawSegs: number[] = []
  let segTruncated = false
  const emitSeg = (ax: number, ay: number, bx: number, by: number) => {
    if (segTruncated) return
    if (rawSegs.length >= SNAP_MAX_SEGMENTS * 4) {
      segTruncated = true
      return
    }
    rawSegs.push(ax, ay, bx, by)
  }
  for (const layer of drawing.layers) {
    if (!layer) continue
    for (const pl of layer.cut ?? []) eachPolylineTarget(pl, emit, emitSeg)
    for (const pl of layer.projection ?? []) eachPolylineTarget(pl, emit, emitSeg)
  }
  if (seen.size === 0) return emptySnapIndex()

  // 2.ª passagem: distribuir pela grelha. A caixa vem dos pontos e não
  // do `bbox` do desenho: as anotações e as cotas podem estar fora, e
  // um ponto fora da grelha nunca seria encontrado.
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const s of seen.values()) {
    if (s.p[0] < minX) minX = s.p[0]
    if (s.p[0] > maxX) maxX = s.p[0]
    if (s.p[1] < minY) minY = s.p[1]
    if (s.p[1] > maxY) maxY = s.p[1]
  }
  const w = Math.max(maxX - minX, 0)
  const h = Math.max(maxY - minY, 0)
  const count = seen.size
  // Célula escolhida para dar ~TARGET_PER_CELL pontos por célula. Com
  // área nula (tudo em linha ou num ponto) cai-se numa célula só, que é
  // o comportamento correcto para tão poucos pontos.
  const area = w * h
  const cell =
    area > 0
      ? Math.max(Math.sqrt((area * TARGET_PER_CELL) / count), 1e-9)
      : Math.max(w, h, 1e-9)
  const cols = Math.max(1, Math.min(4096, Math.floor(w / cell) + 1))
  const rows = Math.max(1, Math.min(4096, Math.floor(h / cell) + 1))
  const buckets = new Map<number, SnapPoint[]>()
  for (const s of seen.values()) {
    const c = Math.min(cols - 1, Math.max(0, Math.floor((s.p[0] - minX) / cell)))
    const r = Math.min(rows - 1, Math.max(0, Math.floor((s.p[1] - minY) / cell)))
    const key = r * cols + c
    const bucket = buckets.get(key)
    if (bucket) bucket.push(s)
    else buckets.set(key, [s])
  }
  const segments = buildSegmentIndex(
    rawSegs,
    segTruncated,
    cell,
    minX,
    minY,
    cols,
    rows,
  )
  return { cell, minX, minY, cols, rows, buckets, count, truncated, segments }
}

function clampIdx(v: number, max: number): number {
  return v < 0 ? 0 : v > max ? max : v
}

/** Distribui os segmentos pelas células que atravessam (travessia de
 *  grelha à Amanatides-Woo). Percorrer a caixa envolvente em vez do
 *  traçado seria O(largura × altura) em células por segmento: uma
 *  diagonal longa encheria milhares de células vazias e o índice ficava
 *  pior do que não ter índice. */
function buildSegmentIndex(
  raw: number[],
  truncated: boolean,
  cell: number,
  minX: number,
  minY: number,
  cols: number,
  rows: number,
): SnapSegmentIndex {
  const count = Math.floor(raw.length / 4)
  if (count === 0) return emptySegmentIndex()
  const coords = new Float64Array(raw)
  const buckets = new Map<number, number[]>()
  const push = (c: number, r: number, seg: number) => {
    const key = r * cols + c
    const b = buckets.get(key)
    if (b) {
      // O traçado nunca volta à célula anterior, logo comparar com o
      // último chega para não repetir.
      if (b[b.length - 1] !== seg) b.push(seg)
    } else {
      buckets.set(key, [seg])
    }
  }
  for (let seg = 0; seg < count; seg++) {
    const o = seg * 4
    const ax = coords[o]
    const ay = coords[o + 1]
    const bx = coords[o + 2]
    const by = coords[o + 3]
    const c0 = clampIdx(Math.floor((ax - minX) / cell), cols - 1)
    const r0 = clampIdx(Math.floor((ay - minY) / cell), rows - 1)
    const c1 = clampIdx(Math.floor((bx - minX) / cell), cols - 1)
    const r1 = clampIdx(Math.floor((by - minY) / cell), rows - 1)
    push(c0, r0, seg)
    if (c0 === c1 && r0 === r1) continue
    const dx = bx - ax
    const dy = by - ay
    const stepC = c1 > c0 ? 1 : c1 < c0 ? -1 : 0
    const stepR = r1 > r0 ? 1 : r1 < r0 ? -1 : 0
    // t em que se atravessa a próxima fronteira de coluna / de linha.
    let tMaxC =
      stepC !== 0
        ? (minX + (c0 + (stepC > 0 ? 1 : 0)) * cell - ax) / dx
        : Infinity
    let tMaxR =
      stepR !== 0
        ? (minY + (r0 + (stepR > 0 ? 1 : 0)) * cell - ay) / dy
        : Infinity
    const tDeltaC = stepC !== 0 ? Math.abs(cell / dx) : Infinity
    const tDeltaR = stepR !== 0 ? Math.abs(cell / dy) : Infinity
    let c = c0
    let r = r0
    for (let guard = 0; guard < MAX_CELLS_PER_SEGMENT; guard++) {
      if (c === c1 && r === r1) break
      if (tMaxC < tMaxR) {
        c += stepC
        tMaxC += tDeltaC
      } else {
        r += stepR
        tMaxR += tDeltaR
      }
      if (c < 0 || c >= cols || r < 0 || r >= rows) break
      push(c, r, seg)
    }
  }
  return {
    coords,
    buckets,
    count,
    truncated,
    seen: new Int32Array(count),
    seenTick: 0,
  }
}

/** Ponto do desenho mais próximo de (x, y) dentro de `radius`, ou null.
 *  Empate na distância decide-se pela prioridade (`SNAP_RANK`). */
export function resolveSnap(
  index: SnapIndex,
  x: number,
  y: number,
  radius: number,
): SnapPoint | null {
  if (!index || index.count === 0) return null
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null
  if (!Number.isFinite(radius) || radius <= 0) return null
  const { cell, minX, minY, cols, rows, buckets } = index
  let c0 = Math.floor((x - radius - minX) / cell)
  let c1 = Math.floor((x + radius - minX) / cell)
  let r0 = Math.floor((y - radius - minY) / cell)
  let r1 = Math.floor((y + radius - minY) / cell)
  c0 = Math.max(0, Math.min(cols - 1, c0))
  c1 = Math.max(0, Math.min(cols - 1, c1))
  r0 = Math.max(0, Math.min(rows - 1, r0))
  r1 = Math.max(0, Math.min(rows - 1, r1))
  if (c1 < c0 || r1 < r0) return null
  // Enquadrado ao ponto de o raio de procura cobrir o desenho todo, o
  // varrimento fica limitado a uma janela à volta do cursor: lá o snap
  // não tem significado e o que importa é devolver depressa.
  if ((c1 - c0 + 1) * (r1 - r0 + 1) > MAX_CELLS_PER_QUERY) {
    const side = Math.max(1, Math.floor(Math.sqrt(MAX_CELLS_PER_QUERY) / 2))
    const cc = Math.max(0, Math.min(cols - 1, Math.floor((x - minX) / cell)))
    const cr = Math.max(0, Math.min(rows - 1, Math.floor((y - minY) / cell)))
    c0 = Math.max(c0, cc - side)
    c1 = Math.min(c1, cc + side)
    r0 = Math.max(r0, cr - side)
    r1 = Math.min(r1, cr + side)
  }
  const r2max = radius * radius
  let best: SnapPoint | null = null
  let bestD2 = Infinity
  // Pontos e segmentos varrem-se na MESMA passagem pelas células: são as
  // mesmas coordenadas de grelha e assim paga-se uma procura no
  // dicionário em vez de duas. `near` fica com os segmentos que passam
  // dentro do disco, que é o filtro que torna os pares baratos: por
  // muito densa que seja a planta, o número de linhas que atravessam um
  // disco de 10 px conta-se pelos dedos.
  const seg = index.segments
  const co = seg.coords
  const withSegs = seg.count > 0
  const near: number[] = []
  const nearD2: number[] = []
  if (withSegs) {
    // Marca de visita: evita a procura linear que seria preciso fazer
    // para não repetir um segmento registado em várias células.
    seg.seenTick = (seg.seenTick + 1) | 0
    if (seg.seenTick === 0) {
      seg.seen.fill(0)
      seg.seenTick = 1
    }
  }
  const tick = seg.seenTick
  for (let r = r0; r <= r1; r++) {
    const rowBase = r * cols
    for (let c = c0; c <= c1; c++) {
      const key = rowBase + c
      const bucket = buckets.get(key)
      if (bucket) {
        for (const s of bucket) {
          const dx = s.p[0] - x
          const dy = s.p[1] - y
          const d2 = dx * dx + dy * dy
          if (d2 > r2max) continue
          if (!best) {
            best = s
            bestD2 = d2
            continue
          }
          if (d2 < bestD2 - TIE_EPS) {
            best = s
            bestD2 = d2
          } else if (
            d2 <= bestD2 + TIE_EPS &&
            SNAP_RANK[s.kind] < SNAP_RANK[best.kind]
          ) {
            best = s
            bestD2 = d2
          }
        }
      }
      if (!withSegs) continue
      const segBucket = seg.buckets.get(key)
      if (!segBucket) continue
      for (const s of segBucket) {
        if (seg.seen[s] === tick) continue
        seg.seen[s] = tick
        const o = s * 4
        const d2 = pointSegDist2(x, y, co[o], co[o + 1], co[o + 2], co[o + 3])
        if (d2 > r2max) continue
        if (near.length < MAX_SEGS_PER_QUERY) {
          near.push(s)
          nearD2.push(d2)
          continue
        }
        // Cheio: entra só se for mais perto do que o pior que lá está.
        let worst = 0
        for (let k = 1; k < nearD2.length; k++) {
          if (nearD2[k] > nearD2[worst]) worst = k
        }
        if (d2 < nearD2[worst]) {
          near[worst] = s
          nearD2[worst] = d2
        }
      }
    }
  }

  // Segunda parte: intersecções. Só se cruzam DEPOIS dos pontos, para o
  // empate com um vértice já encontrado se resolver a favor do vértice
  // sem trabalho extra (é o caso do canto e do T, onde o ponto é o
  // mesmo e mostrar duas marcas não faria sentido).
  {
    const n = near.length
    for (let i = 0; i < n; i++) {
      const oi = near[i] * 4
      const ax = co[oi]
      const ay = co[oi + 1]
      const bx = co[oi + 2]
      const by = co[oi + 3]
      const iMinX = ax < bx ? ax : bx
      const iMaxX = ax < bx ? bx : ax
      const iMinY = ay < by ? ay : by
      const iMaxY = ay < by ? by : ay
      for (let j = i + 1; j < n; j++) {
        const oj = near[j] * 4
        const cx = co[oj]
        const cy = co[oj + 1]
        const dx2 = co[oj + 2]
        const dy2 = co[oj + 3]
        // Caixas que não se tocam nunca se cruzam, e comparar quatro
        // números é muito mais barato do que resolver o sistema. Numa
        // projecção tesselada é isto que descarta a maioria dos pares.
        if (
          (cx < dx2 ? cx : dx2) > iMaxX ||
          (cx < dx2 ? dx2 : cx) < iMinX ||
          (cy < dy2 ? cy : dy2) > iMaxY ||
          (cy < dy2 ? dy2 : cy) < iMinY
        ) {
          continue
        }
        // Segmentos que partilham um extremo encontram-se nesse extremo,
        // que já é vértice: não vale a pena calcular nem duplicar.
        if (
          (ax === cx && ay === cy) ||
          (ax === dx2 && ay === dy2) ||
          (bx === cx && by === cy) ||
          (bx === dx2 && by === dy2)
        ) {
          continue
        }
        const hit = segmentIntersection(ax, ay, bx, by, cx, cy, dx2, dy2)
        if (!hit) continue
        const hx = hit[0]
        const hy = hit[1]
        const ddx = hx - x
        const ddy = hy - y
        const d2 = ddx * ddx + ddy * ddy
        if (d2 > r2max) continue
        if (!best) {
          best = { kind: 'intersection', p: [hx, hy] }
          bestD2 = d2
          continue
        }
        if (d2 < bestD2 - TIE_EPS) {
          best = { kind: 'intersection', p: [hx, hy] }
          bestD2 = d2
        } else if (
          d2 <= bestD2 + TIE_EPS &&
          SNAP_RANK.intersection < SNAP_RANK[best.kind]
        ) {
          best = { kind: 'intersection', p: [hx, hy] }
          bestD2 = d2
        }
      }
    }
  }
  return best
}

/** Quadrado da distância de um ponto a um segmento. */
function pointSegDist2(
  x: number,
  y: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
): number {
  const vx = bx - ax
  const vy = by - ay
  const len2 = vx * vx + vy * vy
  let t = len2 > 0 ? ((x - ax) * vx + (y - ay) * vy) / len2 : 0
  if (t < 0) t = 0
  else if (t > 1) t = 1
  const dx = ax + t * vx - x
  const dy = ay + t * vy - y
  return dx * dx + dy * dy
}

/** Cruzamento de dois segmentos, ou null. Paralelos e colineares dão
 *  null: os primeiros não se encontram e os segundos encontram-se em
 *  infinitos pontos, e um snap que devolvesse "algures nesta
 *  sobreposição" seria pior do que não agarrar nada. O limiar é
 *  RELATIVO ao tamanho dos segmentos, para funcionar tanto num remate
 *  de 2 cm como numa fachada de 80 m. */
function segmentIntersection(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
): Point2 | null {
  const rx = bx - ax
  const ry = by - ay
  const sx = dx - cx
  const sy = dy - cy
  const denom = rx * sy - ry * sx
  const r2 = rx * rx + ry * ry
  const s2 = sx * sx + sy * sy
  // |r × s| = |r||s| sen(ângulo): comparar o quadrado evita duas raízes.
  if (denom * denom <= 1e-24 * r2 * s2) return null
  const qpx = cx - ax
  const qpy = cy - ay
  const t = (qpx * sy - qpy * sx) / denom
  if (t < 0 || t > 1) return null
  const u = (qpx * ry - qpy * rx) / denom
  if (u < 0 || u > 1) return null
  return [ax + t * rx, ay + t * ry]
}

/** Raio de snap em unidades do DESENHO (metros) a partir do raio em
 *  pixéis de ecrã. `svgUnitsPerPx` são milímetros de papel por pixel (o
 *  mesmo `overlayUnit` que faz os glifos não encolherem com o zoom) e
 *  `mmPerMetre` é o `k` do mapeamento (1000/escala). O resultado
 *  acompanha o zoom: 10 px são sempre 10 px no ecrã. */
export function snapRadiusDrawing(
  snapPx: number,
  svgUnitsPerPx: number,
  mmPerMetre: number,
): number {
  if (!Number.isFinite(snapPx) || snapPx <= 0) return 0
  if (!Number.isFinite(svgUnitsPerPx) || svgUnitsPerPx <= 0) return 0
  if (!Number.isFinite(mmPerMetre) || mmPerMetre <= 0) return 0
  return (snapPx * svgUnitsPerPx) / mmPerMetre
}

/** Orto: tranca `p` na horizontal ou na vertical do DESENHO, relativa a
 *  `prev`. Ganha o eixo em que se andou mais, que é a leitura natural do
 *  gesto. Sem `prev` não há relativo a quê, e devolve-se `p`. */
export function orthoPoint(prev: Point2 | null, p: Point2): Point2 {
  if (!prev || !isFinitePoint(prev) || !isFinitePoint(p)) return p
  const dx = p[0] - prev[0]
  const dy = p[1] - prev[1]
  return Math.abs(dx) >= Math.abs(dy) ? [p[0], prev[1]] : [prev[0], p[1]]
}
